
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {LineSegments2} from "../../../libs/three.js/lines/LineSegments2.js";
import {LineSegmentsGeometry} from "../../../libs/three.js/lines/LineSegmentsGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {normalizeStyle, makeColorResolver, buildLegend, listAttributes, isDefaultStyle} from "./LayerStyle.js";

import {t} from "../../digitization/i18n/index.js";
// Colors picked from the LER "Signaturforklaring" (legend) PDF page - see
// tools/parse_ler_gml.py for where the "supply" key on each feature comes
// from. Adjust these if the legend colors need to match more precisely.
export const SUPPLY_COLORS = {
	afloeb: 0x6b5a1e,
	el: 0xe5382c,
	foeringsroer: 0xf2924b,
	telekom: 0x3d8b3d,
	termisk: 0x9b3fc4,
	vand: 0x3355d8,
	supp_info: 0x93ddd3,
	andet: 0x999999,
};

const POINT_MARKER_RADIUS = 0.1;
const COMPONENT_SPHERE_RADIUS = 0.125; // 25cm diameter - half the old 50cm box
// 6 rather than 10: this is a diagrammatic overlay, not a photorealistic
// render, and every side saved is 4 fewer triangles per pipe segment
// (~40% down, from 40 to 24) - real vertex-processing/fill-rate cost given
// a real layer can have several thousand segments, all still visibly
// "round enough" to read as a pipe rather than a hexagonal duct.
const CYLINDER_SEGMENTS = 6;
const DEFAULT_OPACITY = 0.6;

// "Rendered" and "wireframe" both build one real Mesh/LineSegments per
// pipe segment or component the naive way - a real, measured incident:
// even after sharing materials per color, a perfectly normal-sized WFS
// response (~1800 features, ~7000 pipe segments) took ~480MB and several
// seconds to build that way, almost entirely per-OBJECT THREE.js/V8
// overhead (a real Mesh + BufferGeometry + its typed arrays + bounding
// volumes, times several thousand) - enough to crash the tab outright.
// The fix is the standard one for "many small THREE.js objects": build
// each segment/component's geometry as before, but instead of wrapping
// each in its own Mesh, MERGE every same-colored one within an owner into
// a single BufferGeometry (see mergeIndexedGeometries/
// mergeLineGeometriesWithLookup) and add just one Mesh/LineSegments per
// color - a few objects instead of several thousand, for EACH of
// "rendered" and "wireframe". Picking (click/hover for a pipe's info -
// see LERLayer.pickFeatureAt) still needs to resolve back to the ORIGINAL
// feature a merged triangle/line segment came from: each merged mesh
// carries a small sorted table (userData.featureLookup /
// userData.segmentLookup) mapping a hit back to the feature that
// contributed it.
//
// Built lazily (see OwnerGroup.ensureHeavyBuilt) the first time either
// mode is actually requested, but also prewarmed in the background right
// after loading (see LERLayer.prewarmHeavyModes) - now that building is
// cheap, there's no reason to make the user wait for it at the moment
// they first zoom in close enough to actually see it (see gis.vue's own
// zoom-based LOD, which hides both modes again once zoomed back out,
// since the ongoing per-frame draw cost of thousands of real 3D triangles
// is a separate, unavoidable cost this merging doesn't touch).
//
// Every YIELD_EVERY_N_FEATURES features, control is handed back to the
// browser (a bare setTimeout(0)) while accumulating the geometry to merge,
// so building either mode for a genuinely large owner still can't freeze
// the tab.
const YIELD_EVERY_N_FEATURES = 300;
const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));

function colorOf(supply){
	return SUPPLY_COLORS[supply] !== undefined ? SUPPLY_COLORS[supply] : SUPPLY_COLORS.andet;
}

// One shared set of materials per LERLayer (not per feature/mesh):
//  - a MeshBasicMaterial per supply color for "rendered"'s filled pipes/
//    spheres, all at the same single `opacity`;
//  - one shared LineBasicMaterial for "rendered"'s pipe edge/outline
//    overlay, black regardless of supply (as it always was), tied to the
//    very same `opacity` rather than an independent one - one slider, not
//    two, matching how this looked before "rendered" and "wireframe" were
//    ever merged into a single mode;
//  - a LineBasicMaterial per supply color for "wireframe"'s pipe edges and
//    a wireframe:true MeshBasicMaterial per color for its components -
//    both always fully opaque (no transparency at all): wireframe mode
//    has nothing else to show through, so unlike "rendered" it was never
//    given its own opacity control.
class RenderedMaterials{
	constructor(opacity){
		this.opacity = opacity;
		this.fillByColor = new Map();
		this.wireframeLineByColor = new Map();
		this.wireframeSphereByColor = new Map();
		this.edgeOverlayMaterial = new THREE.LineBasicMaterial({color: 0x000000, transparent: true, opacity});
	}

	fillMaterial(color){
		let material = this.fillByColor.get(color);
		if(!material){
			material = new THREE.MeshBasicMaterial({
				color, transparent: true, opacity: this.opacity, depthWrite: false, side: THREE.DoubleSide,
			});
			this.fillByColor.set(color, material);
		}
		return material;
	}

	wireframeLineMaterial(color){
		let material = this.wireframeLineByColor.get(color);
		if(!material){
			material = new THREE.LineBasicMaterial({color});
			this.wireframeLineByColor.set(color, material);
		}
		return material;
	}

	wireframeSphereMaterial(color){
		let material = this.wireframeSphereByColor.get(color);
		if(!material){
			material = new THREE.MeshBasicMaterial({color, wireframe: true});
			this.wireframeSphereByColor.set(color, material);
		}
		return material;
	}

	setOpacity(opacity){
		this.opacity = opacity;
		for(let material of this.fillByColor.values()){
			material.opacity = opacity;
		}
		this.edgeOverlayMaterial.opacity = opacity;
	}

	dispose(){
		for(let material of this.fillByColor.values()){
			material.dispose();
		}
		for(let material of this.wireframeLineByColor.values()){
			material.dispose();
		}
		for(let material of this.wireframeSphereByColor.values()){
			material.dispose();
		}
		this.edgeOverlayMaterial.dispose();
	}
}

// Concatenates several indexed BufferGeometries (already world-transformed
// - see buildPipeSegmentGeometries/buildComponentSphereGeometry, which
// bake position/rotation straight into the vertex data via applyMatrix4/
// translate rather than leaving it to a per-instance Mesh transform) into
// one, tracking which face range each one landed at so a raycast hit can
// be mapped back to its original feature (see featureForFace). Only
// position+normal are kept - every material these geometries are used
// with is an unlit, untextured MeshBasicMaterial, so a uv attribute would
// just be dead weight.
function mergeIndexedGeometries(geometries){
	let vertexCount = 0;
	let indexCount = 0;
	for(let g of geometries){
		vertexCount += g.attributes.position.count;
		indexCount += g.index.count;
	}

	let position = new Float32Array(vertexCount * 3);
	let normal = new Float32Array(vertexCount * 3);
	let index = vertexCount > 65535 ? new Uint32Array(indexCount) : new Uint16Array(indexCount);

	let vOffset = 0;
	let iOffset = 0;
	let faceRanges = []; // [{startFace, faceCount}], same order as `geometries`

	for(let g of geometries){
		position.set(g.attributes.position.array, vOffset * 3);
		normal.set(g.attributes.normal.array, vOffset * 3);

		let srcIndex = g.index.array;
		for(let i = 0; i < srcIndex.length; i++){
			index[iOffset + i] = srcIndex[i] + vOffset;
		}

		faceRanges.push({startFace: iOffset / 3, faceCount: srcIndex.length / 3});
		vOffset += g.attributes.position.count;
		iOffset += srcIndex.length;
	}

	let merged = new THREE.BufferGeometry();
	merged.setIndex(new THREE.BufferAttribute(index, 1));
	merged.setAttribute("position", new THREE.BufferAttribute(position, 3));
	merged.setAttribute("normal", new THREE.BufferAttribute(normal, 3));

	return {geometry: merged, faceRanges};
}

// Concatenates several non-indexed LineSegments geometries (an
// EdgesGeometry only ever carries position, two vertices per line) into
// one, without per-source tracking - used for "rendered"'s pipe edge
// overlay, which (like every pipe's edges, regardless of supply) was
// never independently pickable to begin with - see
// LERLayer.getPickableMeshes's own comment on that.
function mergeLineGeometries(geometries){
	let vertexCount = 0;
	for(let g of geometries){
		vertexCount += g.attributes.position.count;
	}

	let position = new Float32Array(vertexCount * 3);
	let vOffset = 0;
	for(let g of geometries){
		position.set(g.attributes.position.array, vOffset * 3);
		vOffset += g.attributes.position.count;
	}

	let merged = new THREE.BufferGeometry();
	merged.setAttribute("position", new THREE.BufferAttribute(position, 3));
	return merged;
}

// Same as mergeLineGeometries, but also tracks which line-segment range
// each source geometry landed at (a LineSegments raycast hit reports
// `index` = the hit segment's first of its 2 vertices - see
// featureForSegment) - used for "wireframe"'s pipe edges, which (unlike
// "rendered"'s overlay) ARE meant to be pickable on their own, since
// "wireframe" is a real, independent display mode rather than a
// supplementary overlay on a filled mesh.
function mergeLineGeometriesWithLookup(geometries){
	let vertexCount = 0;
	for(let g of geometries){
		vertexCount += g.attributes.position.count;
	}

	let position = new Float32Array(vertexCount * 3);
	let vOffset = 0;
	let segmentRanges = []; // [{startSegment, segmentCount}], same order as `geometries`

	for(let g of geometries){
		position.set(g.attributes.position.array, vOffset * 3);
		let vCount = g.attributes.position.count;
		segmentRanges.push({startSegment: vOffset / 2, segmentCount: vCount / 2});
		vOffset += vCount;
	}

	let merged = new THREE.BufferGeometry();
	merged.setAttribute("position", new THREE.BufferAttribute(position, 3));
	return {geometry: merged, segmentRanges};
}

// Binary search: which of a merged mesh's original features contributed
// the triangle at `faceIndex` (as reported by a Raycaster intersection
// against an indexed mesh). `faceRanges` is sorted ascending by startFace
// by construction.
function featureForFace(faceRanges, features, faceIndex){
	let lo = 0;
	let hi = faceRanges.length - 1;
	while(lo <= hi){
		let mid = (lo + hi) >> 1;
		let range = faceRanges[mid];
		if(faceIndex < range.startFace){
			hi = mid - 1;
		}else if(faceIndex >= range.startFace + range.faceCount){
			lo = mid + 1;
		}else{
			return features[mid];
		}
	}
	return null;
}

// Same idea, for a LineSegments raycast hit: `intersection.index` is the
// first of the two vertices making up the hit segment, so the segment's
// own index is index/2 (confirmed against this three.js build directly -
// two 2-vertex segments at vertex offsets 0 and 2 raycast back index 0
// and 2 respectively, i.e. segmentIndex = index/2, not a running segment
// counter).
function featureForSegment(segmentRanges, features, segmentIndex){
	let lo = 0;
	let hi = segmentRanges.length - 1;
	while(lo <= hi){
		let mid = (lo + hi) >> 1;
		let range = segmentRanges[mid];
		if(segmentIndex < range.startSegment){
			hi = mid - 1;
		}else if(segmentIndex >= range.startSegment + range.segmentCount){
			lo = mid + 1;
		}else{
			return features[mid];
		}
	}
	return null;
}

// A straight pipe segment's geometry between two points, with its TOP
// surface at each point's given Z (not its center) - matching LE34's
// "Z = overkant rør" convention for rendered pipes/cables. Returns the
// fill geometry and its edge/outline geometry (the latter reused for BOTH
// "rendered"'s edge overlay and "wireframe"'s own line - the geometry
// itself is identical either way, only the material differs, so this is
// computed once regardless of how many of the two modes end up using it),
// both already transformed into their final world position/orientation
// (no Mesh, no material - this owner merges many of these together, see
// the module comment) - null if the two points coincide (nothing to
// build).
//
// Unlit MeshBasicMaterial (assigned by the caller, not here) rather than a
// lit one: Potree's scene has no THREE.Light set up (its point clouds use
// their own shaders, not scene lighting), so a lit material renders these
// solids black - and even with a light added, a pipe's cylindrical side
// normally points sideways, catching only a grazing angle from any single
// light and looking black anyway. Flat, unlit color is what a diagrammatic
// utility overlay like this actually wants: the true legend color, always.
function buildPipeSegmentGeometries(p1, p2, radius){
	let start = new THREE.Vector3(p1[0], p1[1], p1[2] - radius);
	let end = new THREE.Vector3(p2[0], p2[1], p2[2] - radius);

	let direction = new THREE.Vector3().subVectors(end, start);
	let length = direction.length();
	if(length < 1e-6){
		return null;
	}

	let position = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
	let quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
	let matrix = new THREE.Matrix4().compose(position, quaternion, new THREE.Vector3(1, 1, 1));

	// The *BufferGeometry variant specifically (this three.js build still
	// has the old dual Geometry/BufferGeometry split) - CylinderGeometry
	// here is the legacy, non-buffer Geometry class, with no .attributes/
	// .index of its own for mergeIndexedGeometries to read.
	let fill = new THREE.CylinderBufferGeometry(radius, radius, length, CYLINDER_SEGMENTS);
	fill.applyMatrix4(matrix);

	// A rigid transform (rotation + translation, no scale) doesn't change
	// any angle between adjacent faces, so computing the edges after
	// applying it is exactly as correct as before and saves transforming
	// twice.
	let edges = new THREE.EdgesGeometry(fill, 1);

	return {fill, edges};
}

// A 25cm sphere standing in for a component (valve, manhole, cabinet, ...),
// with its TOP at the given Z, same top-referenced convention as pipes -
// baked directly into the geometry (translate) rather than left to a
// per-instance Mesh transform, so many of these can be merged the same way
// as pipe segments. "Rendered" and "wireframe" each get their own copy
// (rather than sharing one, the way a single component's fill+wireframe
// mesh used to) - the two modes' geometry now has separate, independent
// build/dispose lifecycles (see OwnerGroup.rebuildHeavyModes), and
// duplicating one small sphere per component is a trivial cost next to
// what that independence is worth in code simplicity.
function buildComponentSphereGeometry(point, radius = COMPONENT_SPHERE_RADIUS){
	// SphereBufferGeometry, not SphereGeometry - see buildPipeSegmentGeometries's
	// own comment on why the *BufferGeometry variant is required here.
	let geometry = new THREE.SphereBufferGeometry(radius, 16, 12);
	geometry.translate(point[0], point[1], point[2] - radius);
	return geometry;
}

function buildSimpleLine(coords, color, dashed, material = null){
	let positions = [];
	for(let [x, y, z] of coords){
		positions.push(x, y, z);
	}

	let geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));

	if(!material){
		material = dashed
			? new THREE.LineDashedMaterial({color, dashSize: 0.5, gapSize: 0.3})
			: new THREE.LineBasicMaterial({color});
	}

	let line = new THREE.Line(geometry, material);
	if(dashed){
		line.computeLineDistances();
	}

	return line;
}

// Tegner ikke noget selv: den tynde linje under en bred linje (se buildWideLineVisual), så den stadig kan vælges med musen
const PICK_ONLY_MATERIAL = new THREE.LineBasicMaterial({visible: false});
const sizeScratch = new THREE.Vector2();

// En linje med en bredde i pixels (LineSegments2), som hænger under den tynde linje, der bærer objektet. Opløsningen (skærmstørrelsen),
// som bredden regnes ud fra, sættes af tegningen selv, hver gang linjen tegnes.
function buildWideLineVisual(coords, material){
	let positions = [];
	for(let i = 0; i < coords.length - 1; i++){
		positions.push(coords[i][0], coords[i][1], coords[i][2], coords[i + 1][0], coords[i + 1][1], coords[i + 1][2]);
	}
	let geometry = new LineSegmentsGeometry();
	geometry.setPositions(positions);
	let line = new LineSegments2(geometry, material);
	if(material.dashed){
		line.computeLineDistances();
	}
	line.onBeforeRender = (renderer) => {
		renderer.getSize(sizeScratch);
		material.resolution.set(sizeScratch.x, sizeScratch.y);
	};
	return line;
}

function buildSimplePoint(point, color, radius = POINT_MARKER_RADIUS){
	let geometry = new THREE.SphereGeometry(radius, 8, 6);
	let material = new THREE.MeshBasicMaterial({color});
	let mesh = new THREE.Mesh(geometry, material);
	mesh.position.set(...point);
	return mesh;
}

// Disposes every geometry under `object` (traverse recurses into
// children), deduplicated in case the same geometry object was ever added
// to more than one mesh. Materials are never touched here - they're
// shared at the LERLayer level (see RenderedMaterials) and disposed once,
// for the whole layer, in LERLayer.dispose().
function disposeGeometries(object){
	let disposed = new Set();
	object.traverse(child => {
		if(child.geometry && !disposed.has(child.geometry)){
			child.geometry.dispose();
			disposed.add(child.geometry);
		}
	});
}

// One utility owner's data, in three switchable representations - a
// "simple" one (thin lines/points, always built immediately - see
// addFeature), a "rendered" one (true-size filled pipes/spheres, with a
// black edge/outline overlay on pipes, all at one shared opacity), and a
// "wireframe" one (the same true-size pipes/spheres, outline only, always
// fully opaque) - both of the latter merged per color rather than one
// Mesh per segment/component (see the module comment), built lazily the
// first time either is actually requested (see ensureHeavyBuilt) - plus
// traces (always drawn dashed regardless of mode - a Ledningstrace is
// inherently an approximate corridor, not surveyed geometry, so rendering
// it as a precise solid pipe would overstate how well it's actually
// known).
class OwnerGroup extends THREE.Object3D{

	constructor(name, source, materials){
		super();

		this.name = name;
		this.enabled = true;
		// "ler" (the official LER GML/WFS) or "custom" (a customer WFS
		// layer added under Custom GIS data - see LERParser.extractGenericWFSPart,
		// which buckets each such layer as its own synthetic "owner") - lets
		// index.html show/remove each source's own layers in its own
		// section without a second, separate tracking structure.
		this.source = source;
		this.materials = materials;

		this.simpleGroup = new THREE.Object3D();
		this.renderedGroup = new THREE.Object3D();
		// "Rendered"'s pipe edge overlay - kept in a nested, non-Mesh
		// sub-object precisely so it DOESN'T show up as a direct child of
		// renderedGroup: getPickableMeshes() only ever takes a group's
		// direct children (a plain, non-recursive raycast), matching how
		// this overlay has never been independently pickable (a "rendered"
		// pipe's edges are a decoration on the filled mesh, not their own
		// thing - "wireframe" mode below is the real, pickable outline).
		this.renderedOverlayGroup = new THREE.Object3D();
		this.renderedGroup.add(this.renderedOverlayGroup);
		this.wireframeGroup = new THREE.Object3D();
		this.traceGroup = new THREE.Object3D();

		this.add(this.simpleGroup, this.renderedGroup, this.wireframeGroup, this.traceGroup);

		this.features = []; // every non-trace feature ever added - see rebuildHeavyModes
		this.allFeatures = []; // ... og traces - til stilarten (farver efter attribut ser på hele laget)
		this.heavyBuilt = false; // "rendered" + "wireframe" - see ensureHeavyBuilt
		this.style = normalizeStyle(null); // stilart (farve/bredde/størrelse), se LayerStyle.js og setStyle
		this.colorFn = null; // null = lagets egne farver efter forsyningsart
		this.wideMaterials = new Map();
		this._heavyQueue = Promise.resolve();
	}

	colorFor(feature){
		return this.colorFn ? this.colorFn(feature) : colorOf(feature.supply);
	}

	wideMaterial(color){
		let style = this.style;
		let key = color + "|" + style.lineWidth + "|" + style.dashed;
		let material = this.wideMaterials.get(key);
		if(!material){
			material = new LineMaterial({
				color, linewidth: style.lineWidth, dashed: style.dashed, dashSize: 0.5, gapSize: 0.3,
				resolution: new THREE.Vector2(1000, 1000),
			});
			if(style.dashed){
				// denne udgave af LineMaterial slår stiplet til med et define, ikke med egenskaben alene
				material.defines.USE_DASH = "";
				material.needsUpdate = true;
			}
			this.wideMaterials.set(key, material);
		}
		return material;
	}

	disposeWideMaterials(){
		for(let material of this.wideMaterials.values()){
			material.dispose();
		}
		this.wideMaterials.clear();
	}

	// Den enkle visning (og trace) af ét objekt, i lagets stilart
	buildSimpleFor(feature){
		let color = this.colorFor(feature);
		let style = this.style;
		let tag = (object) => { object.userData.lerFeature = feature; return object; };

		if(feature.is_trace){
			this.traceGroup.add(tag(buildSimpleLine(feature.coords, color, true)));
			return;
		}

		if(feature.geometry === "line"){
			if(style.lineWidth > 1 && feature.coords.length > 1){
				let pick = tag(buildSimpleLine(feature.coords, color, false, PICK_ONLY_MATERIAL));
				pick.add(buildWideLineVisual(feature.coords, this.wideMaterial(color)));
				this.simpleGroup.add(pick);
			}else{
				this.simpleGroup.add(tag(buildSimpleLine(feature.coords, color, style.dashed)));
			}
		}else{
			this.simpleGroup.add(tag(buildSimplePoint(feature.coords[0], color, POINT_MARKER_RADIUS * style.pointSize)));
		}
	}

	// Ny stilart til laget: farver pr. objekt, bredde, stiplet og punktstørrelse. Bygger den enkle visning og trace om og - hvis
	// rør/komponenter allerede er bygget - de tunge visninger.
	async setStyle(style){
		this.style = normalizeStyle(style);
		await this.restyle();
	}

	async restyle(){
		this.colorFn = makeColorResolver(this.allFeatures, this.style, null);
		for(let group of [this.simpleGroup, this.traceGroup]){
			disposeGeometries(group);
			for(let child of [...group.children]){
				group.remove(child);
			}
		}
		this.disposeWideMaterials();
		for(let feature of this.allFeatures){
			this.buildSimpleFor(feature);
		}
		if(this.heavyBuilt){
			await this.rebuildHeavyModes();
		}
	}

	getStyle(){
		return normalizeStyle(this.style);
	}

	getLegend(){
		return buildLegend(this.allFeatures, this.style);
	}

	getAttributes(){
		return listAttributes(this.allFeatures);
	}

	hasGeometry(kind){
		return this.allFeatures.some(f => !f.is_trace && f.geometry === kind);
	}

	addFeature(feature){
		this.allFeatures.push(feature);
		this.buildSimpleFor(feature);

		if(feature.is_trace){
			return;
		}

		this.features.push(feature);

		if(this.heavyBuilt){
			// A feature added after "rendered"/"wireframe" were already
			// built (e.g. a second Custom-GIS fetch onto an
			// already-displayed layer) - rebuilds the merged geometry from
			// everything seen so far. Rare in practice, and simpler/safer
			// than trying to append into an already-merged BufferGeometry
			// in place.
			this.rebuildHeavyModes();
		}
	}

	applyVisibility(mode){
		this.traceGroup.visible = this.enabled;
		this.simpleGroup.visible = this.enabled && mode === "simple";
		this.renderedGroup.visible = this.enabled && mode === "rendered";
		this.wireframeGroup.visible = this.enabled && mode === "wireframe";
	}

	// Builds "rendered" and "wireframe" together, the first time either is
	// actually requested - a no-op once already built (see addFeature for
	// how later additions are handled instead). Built together (rather
	// than two independently-lazy paths) since both are zoom-gated by the
	// very same threshold in practice (see gis.vue) and both get
	// prewarmed together right after loading anyway - there's little to
	// gain from tracking them separately, and doing so would mean walking
	// this.features twice.
	async ensureHeavyBuilt(){
		if(this.heavyBuilt){
			return;
		}
		this.heavyBuilt = true;
		await this.rebuildHeavyModes();
	}

	// (Re)builds "rendered" and "wireframe" from this.features - see the
	// module comment for why this merges every same-colored pipe segment/
	// component into a small, fixed number of THREE.js objects per mode
	// instead of one per feature/segment. Yields periodically while
	// accumulating (same technique as elsewhere in this file) so a large
	// owner's (re)build still can't freeze the tab.
	rebuildHeavyModes(){
		this._heavyQueue = this._heavyQueue.then(() => this._rebuildHeavyModesNow());
		return this._heavyQueue;
	}

	async _rebuildHeavyModesNow(){
		let sphereRadius = COMPONENT_SPHERE_RADIUS * this.style.pointSize;
		disposeGeometries(this.renderedGroup);
		disposeGeometries(this.wireframeGroup);
		for(let child of [...this.renderedGroup.children]){
			this.renderedGroup.remove(child);
		}
		for(let child of [...this.wireframeGroup.children]){
			this.wireframeGroup.remove(child);
		}
		this.renderedOverlayGroup = new THREE.Object3D();
		this.renderedGroup.add(this.renderedOverlayGroup);

		let bucket = (map, key) => {
			let entry = map.get(key);
			if(!entry){
				entry = {geometries: [], features: []};
				map.set(key, entry);
			}
			return entry;
		};

		let pipeFillByColor = new Map();
		let pipeWireframeByColor = new Map();
		let pipeEdgeGeometries = []; // rendered's overlay - flat, no per-feature tracking (never pickable)
		let sphereByColor = new Map(); // rendered's filled components
		let sphereWireframeByColor = new Map(); // wireframe's components

		let i = 0;
		for(let feature of this.features){
			let color = this.colorFor(feature);

			if(feature.geometry === "line"){
				let radius = Math.max(feature.diameter_m || 0.1, 0.02) / 2;
				for(let j = 0; j < feature.coords.length - 1; j++){
					let built = buildPipeSegmentGeometries(feature.coords[j], feature.coords[j + 1], radius);
					if(!built){
						continue;
					}

					let fillBucket = bucket(pipeFillByColor, color);
					fillBucket.geometries.push(built.fill);
					fillBucket.features.push(feature);

					// The same edges geometry feeds both "rendered"'s overlay
					// and "wireframe"'s own line - see buildPipeSegmentGeometries.
					pipeEdgeGeometries.push(built.edges);
					let wireBucket = bucket(pipeWireframeByColor, color);
					wireBucket.geometries.push(built.edges);
					wireBucket.features.push(feature);
				}
			}else{
				let fillBucket = bucket(sphereByColor, color);
				fillBucket.geometries.push(buildComponentSphereGeometry(feature.coords[0], sphereRadius));
				fillBucket.features.push(feature);

				let wireBucket = bucket(sphereWireframeByColor, color);
				wireBucket.geometries.push(buildComponentSphereGeometry(feature.coords[0], sphereRadius));
				wireBucket.features.push(feature);
			}

			i++;
			if(i % YIELD_EVERY_N_FEATURES === 0){
				await yieldToBrowser();
			}
		}

		// "Rendered": filled pipes + spheres, plus the pipe edge overlay.
		for(let [color, {geometries, features}] of pipeFillByColor){
			let {geometry, faceRanges} = mergeIndexedGeometries(geometries);
			let mesh = new THREE.Mesh(geometry, this.materials.fillMaterial(color));
			mesh.userData.featureLookup = {faceRanges, features};
			this.renderedGroup.add(mesh);
		}
		if(pipeEdgeGeometries.length > 0){
			let geometry = mergeLineGeometries(pipeEdgeGeometries);
			this.renderedOverlayGroup.add(new THREE.LineSegments(geometry, this.materials.edgeOverlayMaterial));
		}
		for(let [color, {geometries, features}] of sphereByColor){
			let {geometry, faceRanges} = mergeIndexedGeometries(geometries);
			let mesh = new THREE.Mesh(geometry, this.materials.fillMaterial(color));
			mesh.userData.featureLookup = {faceRanges, features};
			this.renderedGroup.add(mesh);
		}

		// "Wireframe": pipe outlines (pickable) + component wireframes (pickable).
		for(let [color, {geometries, features}] of pipeWireframeByColor){
			let {geometry, segmentRanges} = mergeLineGeometriesWithLookup(geometries);
			let lines = new THREE.LineSegments(geometry, this.materials.wireframeLineMaterial(color));
			lines.userData.segmentLookup = {segmentRanges, features};
			this.wireframeGroup.add(lines);
		}
		for(let [color, {geometries, features}] of sphereWireframeByColor){
			let {geometry, faceRanges} = mergeIndexedGeometries(geometries);
			let mesh = new THREE.Mesh(geometry, this.materials.wireframeSphereMaterial(color));
			mesh.userData.featureLookup = {faceRanges, features};
			this.wireframeGroup.add(mesh);
		}

		// Every geometry handed to a merge function above was copied into
		// that merged buffer, not referenced by it - safe to dispose all
		// of them now regardless of how many merges each one fed into
		// (pipe edges fed both the rendered-overlay merge and their own
		// per-color wireframe merge).
		for(let g of pipeEdgeGeometries){
			g.dispose();
		}
		for(let map of [pipeFillByColor, sphereByColor, sphereWireframeByColor]){
			for(let {geometries} of map.values()){
				for(let g of geometries){
					g.dispose();
				}
			}
		}
	}
}

// Renders a parsed LER graveforespoergsel (tools/parse_ler_gml.py output):
// utility owners' lines and components, toggleable per owner, switchable
// between a lightweight line/point view, a true-to-size filled pipe/
// sphere view (adjustable opacity), and a true-to-size wireframe-only
// view.
export class LERLayer extends THREE.Object3D{

	// source tags every owner this constructs from data.features - "ler"
	// (the default) for the official LER GML/WFS, or "custom" when a
	// customer WFS fetch happens to be the very first thing loaded (no
	// LERLayer existing yet to addFeatures(part, "custom") onto instead).
	constructor(data, source = "ler"){
		super();

		this.name = "ler_layer";
		this.data = data;
		this.mode = "simple";
		this.opacity = DEFAULT_OPACITY;
		this.materials = new RenderedMaterials(this.opacity);
		this.owners = new Map();

		this.position.set(...data.offset);

		for(let feature of data.features){
			this.getOwnerGroup(feature.owner, source).addFeature(feature);
		}

		// "simple" (the default here) needs no async build - see setMode -
		// so this fire-and-forget call (a constructor can't itself be
		// async) resolves with nothing left to do.
		this.setMode(this.mode);
	}

	getOwnerGroup(ownerId, source = "ler"){
		if(!this.owners.has(ownerId)){
			let name = this.data.owners[ownerId] || ownerId || t("Ukendt");
			let group = new OwnerGroup(name, source, this.materials);
			this.owners.set(ownerId, group);
			this.add(group);
		}

		return this.owners.get(ownerId);
	}

	// Adds more features (e.g. a customer WFS layer fetched later - see
	// LERParser.extractGenericWFSPart / extractLERPart) to this *existing*
	// layer, reusing its already-fixed offset (this.position) rather than
	// recomputing one - so a second source lines up with whatever's
	// already loaded instead of needing the whole layer rebuilt. source
	// tags any *newly created* owner entries this call introduces (an
	// owner id that already exists keeps whatever source it was first
	// created with).
	async addFeatures(part, source = "ler"){
		let offset = this.position.toArray();
		Object.assign(this.data.owners, part.owners);

		for(let feature of part.features){
			feature.coords = feature.coords.map(([x, y, z]) => [x - offset[0], y - offset[1], z - offset[2]]);
			this.getOwnerGroup(feature.owner, source).addFeature(feature);
		}

		// et lag med egen stilart: nye objekter skal med i farveklasserne
		for(let group of this.owners.values()){
			if(!isDefaultStyle(group.style) && group.allFeatures.length > 0){
				await group.restyle();
			}
		}

		await this.setMode(this.mode);
	}

	// Builds "rendered"/"wireframe" for every owner the first time either
	// is actually requested (a no-op for owners that already have it -
	// see OwnerGroup.ensureHeavyBuilt), then applies the mode's
	// visibility. Async, and worth awaiting from a caller that wants to
	// know once a large layer's first switch has actually finished
	// building - but not required to: a fire-and-forget call still ends
	// up in the right state, just possibly a frame or two later.
	async setMode(mode){
		this.mode = mode;

		if(mode === "rendered" || mode === "wireframe"){
			for(let group of this.owners.values()){
				await group.ensureHeavyBuilt();
			}
		}

		for(let group of this.owners.values()){
			group.applyVisibility(mode);
		}
	}

	// Builds "rendered"/"wireframe" for every owner right away, WITHOUT
	// touching mode/visibility - lets a caller build them in the
	// background the moment data loads (now cheap - see the module
	// comment: merged geometry dropped this from ~640MB/7s to ~5MB/1.3s
	// for a normal WFS response) so that by the time the user actually
	// zooms in close enough for either to show (see the caller's own
	// zoom-based LOD), they're already sitting there ready rather than
	// needing to build right at that moment. ensureHeavyBuilt() is
	// idempotent per owner, so calling this more than once (e.g. after
	// addFeatures introduces a new owner) only builds what isn't already
	// built.
	async prewarmHeavyModes(){
		for(let group of this.owners.values()){
			await group.ensureHeavyBuilt();
		}
	}

	// Opacity of "rendered"'s filled pipes/spheres AND its pipe edge
	// overlay together (always the same value, as it was before the two
	// were briefly split into independent sliders - see git history).
	// "wireframe", the simple line/point view, and traces are always
	// fully opaque.
	setOpacity(opacity){
		this.opacity = opacity;
		this.materials.setOpacity(opacity);
	}

	// Stilart pr. lag/owner (se LayerStyle.js). getOwnerStyleInfo giver alt dialogen skal bruge på én gang.
	getOwnerStyleInfo(ownerId){
		let group = this.owners.get(ownerId);
		if(!group){
			return null;
		}
		return {
			style: group.getStyle(),
			legend: group.getLegend(),
			attributes: group.getAttributes(),
			count: group.allFeatures.length,
			hasLines: group.hasGeometry("line"),
			hasPoints: group.hasGeometry("point"),
		};
	}

	async setOwnerStyle(ownerId, style){
		let group = this.owners.get(ownerId);
		if(group){
			await group.setStyle(style);
			group.applyVisibility(this.mode);
		}
	}

	setOwnerEnabled(ownerId, enabled){
		let group = this.owners.get(ownerId);
		if(group){
			group.enabled = enabled;
			group.applyVisibility(this.mode);
		}
	}

	// [{id, name, enabled, source}], sorted by name, for building a UI list
	listOwners(){
		return [...this.owners.entries()]
			.map(([id, group]) => ({id, name: group.name, enabled: group.enabled, source: group.source}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	// Removes one owner/layer entirely (not just hides it) - disposing its
	// geometries so a removed layer doesn't linger as a memory leak.
	// Materials are NOT disposed here - they're shared across every owner
	// in this layer (see RenderedMaterials) and are disposed once, for the
	// whole layer, in dispose() below.
	removeOwner(ownerId){
		let group = this.owners.get(ownerId);
		if(!group){
			return;
		}

		this.remove(group);
		disposeGeometries(group);
		group.disposeWideMaterials();

		this.owners.delete(ownerId);
		delete this.data.owners[ownerId];
	}

	// Every mesh/line currently actually visible (respecting both per-owner
	// enable/disable and the mode toggle - traces are included either way,
	// since they're always shown), for anything that needs to raycast
	// against "whatever the user can currently see" - both pickFeatureAt's
	// own info-tooltip picking and, via viewer.scene.pickableMeshes (see
	// index.html), navigation's click/scroll-to-zoom, which otherwise only
	// ever sees the point cloud. "Rendered"'s pipe edge overlay is
	// deliberately never included - see OwnerGroup's own
	// renderedOverlayGroup comment; "wireframe"'s own lines/spheres ARE
	// included, since that's a real, independently pickable display mode.
	getPickableMeshes(){
		let candidates = [];

		for(let group of this.owners.values()){
			if(!group.enabled){
				continue;
			}

			candidates.push(...group.traceGroup.children);
			let modeGroup = this.mode === "simple" ? group.simpleGroup
				: this.mode === "wireframe" ? group.wireframeGroup
				: group.renderedGroup;
			candidates.push(...modeGroup.children);
		}

		return candidates;
	}

	// Raycasts against whatever's currently actually visible and returns
	// the hit feature's data, or null. A "rendered"/"wireframe" hit lands
	// on a merged mesh or LineSegments covering many original features
	// (see the module comment) - resolved back to the specific one via
	// its own featureLookup (indexed mesh: face-based) or segmentLookup
	// (LineSegments: segment-based) table; anything else (a "simple"-mode
	// line/point, or a trace) still carries its single feature directly
	// on userData.lerFeature, as before.
	pickFeatureAt(raycaster){
		let hits = raycaster.intersectObjects(this.getPickableMeshes(), false);
		if(hits.length === 0){
			return null;
		}

		let hit = hits[0];
		let faceLookup = hit.object.userData.featureLookup;
		if(faceLookup){
			return featureForFace(faceLookup.faceRanges, faceLookup.features, hit.faceIndex);
		}

		let segmentLookup = hit.object.userData.segmentLookup;
		if(segmentLookup){
			return featureForSegment(segmentLookup.segmentRanges, segmentLookup.features, Math.floor(hit.index / 2));
		}

		return hit.object.userData.lerFeature || null;
	}

	getOwnerName(ownerId){
		return this.data.owners[ownerId] || ownerId || t("Ukendt");
	}

	dispose(){
		this.materials.dispose();
		for(let group of this.owners.values()){
			group.disposeWideMaterials();
		}
		if(this.parent){
			this.parent.remove(this);
		}
	}
}

export async function loadLERLayer(url){
	let response = await fetch(url);

	if(!response.ok){
		console.warn(`LERLayer: could not load ${url} (${response.status})`);
		return null;
	}

	let data = await response.json();

	return new LERLayer(data);
}
